/**
 * tabsProvider
 *
 * @author Dec Norton <decnorton@gmail.com>
 */

import { findObjectWithKeyIndex } from '@shared/utils/arrays';
import get from 'lodash-es/get';

export function tabsProvider() {
    this.$get = $get;

    $get.$inject = ['$state', '$stateParams', '$transitions'];

    function $get($state, $stateParams, $transitions) {
        return {
            attach,
            tab,
        };

        /**
         * Attach to the scope.
         * @param {Object} scope
         * @param {Object[]} tabs
         * @return {void}
         */
        function attach(scope, tabs = []) {
            if (!scope) {
                throw '$scope is undefined';
            }

            const state = (scope.tabManager = {
                tabs: tabs.filter(Boolean),
                currentTab: undefined
            });

            // Current state
            const currentState = $state.current;
            const parentState = currentState.$$state().parent;
            const parentStateName = parentState.name;

            _onStateChanged($state.current);

            $transitions.onEnter({ to: parentStateName + '.**' }, (transition, toState) => _onStateChanged(toState));
            $transitions.onError({ exiting: parentStateName + '.**' }, transition =>
                _onStateChanged(transition.from()),
            );

            // Listeners
            if (tabs.length > 0) {
                scope.$watch('tabManager.currentTab', watchCurrentTab.bind(scope));
            }

            function _onStateChanged(toState) {
                const tab = get(toState, 'data.tab');

                if (!tab) {
                    return;
                }

                if (tabs.length) {
                    // -1 is returned if not found
                    state.currentTab = Math.max(findObjectWithKeyIndex(state.tabs, 'key', tab), 0);
                } else {
                    state.currentTab = tab;
                }
            }
        }

        /**
         * Create a new tab object.
         * @param  {string} key   The name of the tab.
         * @param  {string} state The state it should be linked to.
         * @return {Object}
         */
        function tab(key, state) {
            return {
                key,
                state,
            };
        }

        /**
         * Returns a listener that is used to listen for changes to the current tab.
         *
         * @param newValue
         * @param oldValue
         * @return {void}
         */
        function watchCurrentTab(newValue, oldValue) {
            // Quit early if: the values are the same, newValue == null (likely pressed the back
            // button), or oldValue == null (fresh page load at /users/:id)
            if (newValue === oldValue || newValue === null || oldValue === null) {
                return;
            }

            const tab = this.tabManager.tabs[newValue];

            if (!tab) {
                throw 'Tab is not defined! Index: ' + newValue;
            }

            $state.go(tab.state, {
                id: $stateParams.id,
            });
        }
    }
}
